(() => {
  if (customElements.get('rudraksha-finder')) return;

  class RudrakshaFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.startForm = this.querySelector('[data-rf-start-form]');
      this.profileForm = this.querySelector('[data-rf-profile-form]');
      this.nameInput = this.querySelector('[data-rf-name]');
      this.dateInput = this.querySelector('[data-rf-date]');
      this.timeInput = this.querySelector('[data-rf-time]');
      this.timeUnsureInput = this.querySelector('[data-rf-time-unsure]');
      this.startError = this.querySelector('[data-rf-start-error]');
      this.profileError = this.querySelector('[data-rf-profile-error]');
      this.profileHeading = this.querySelector('[data-rf-profile-heading]');
      this.generateButton = this.querySelector('[data-rf-generate]');
      this.editButton = this.querySelector('[data-rf-edit]');
      this.details = {};
      this.recommendations = this.readRecommendations();

      if (!this.startForm || !this.profileForm) return;

      this.handleStartSubmit = this.handleStartSubmit.bind(this);
      this.handleProfileSubmit = this.handleProfileSubmit.bind(this);
      this.handleTimeUnsure = this.handleTimeUnsure.bind(this);
      this.showStartStep = this.showStartStep.bind(this);

      this.startForm.addEventListener('submit', this.handleStartSubmit);
      this.profileForm.addEventListener('submit', this.handleProfileSubmit);
      this.timeUnsureInput?.addEventListener('change', this.handleTimeUnsure);
      this.editButton?.addEventListener('click', this.showStartStep);

      this.querySelectorAll('input, select').forEach((field) => {
        field.addEventListener('input', () => this.clearErrors());
        field.addEventListener('change', () => this.clearErrors());
      });

      this.setMaximumBirthDate();
      this.handleTimeUnsure();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.startForm?.removeEventListener('submit', this.handleStartSubmit);
      this.profileForm?.removeEventListener('submit', this.handleProfileSubmit);
      this.timeUnsureInput?.removeEventListener('change', this.handleTimeUnsure);
      this.editButton?.removeEventListener('click', this.showStartStep);
      this.initialized = false;
    }

    setMaximumBirthDate() {
      if (!this.dateInput) return;
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      this.dateInput.max = `${year}-${month}-${day}`;
    }

    readRecommendations() {
      const data = this.querySelector('[data-rf-recommendations]');
      if (!data) return [];

      try {
        const recommendations = JSON.parse(data.textContent);
        return Array.isArray(recommendations)
          ? recommendations.filter((item) => item && item.url)
          : [];
      } catch (error) {
        return [];
      }
    }

    handleTimeUnsure() {
      if (!this.timeInput || !this.timeUnsureInput) return;
      const timeIsUnknown = this.timeUnsureInput.checked;
      this.timeInput.disabled = timeIsUnknown;
      this.timeInput.required = !timeIsUnknown;
      if (timeIsUnknown) this.timeInput.value = '';
      this.clearErrors();
    }

    handleStartSubmit(event) {
      event.preventDefault();
      this.clearErrors();

      if (!this.startForm.checkValidity()) {
        this.showError(this.startError, 'Please complete your name, date of birth, and birth time.');
        this.startForm.reportValidity();
        return;
      }

      this.details = {
        name: this.nameInput.value.trim(),
        date: this.dateInput.value,
        time: this.timeUnsureInput.checked ? '' : this.timeInput.value,
        timeUnknown: this.timeUnsureInput.checked
      };

      this.updateSummary();
      this.startForm.hidden = true;
      this.profileForm.hidden = false;
      this.profileHeading?.focus({ preventScroll: true });
      this.scrollIntoViewIfNeeded();
    }

    handleProfileSubmit(event) {
      event.preventDefault();
      this.clearErrors();

      if (!this.profileForm.checkValidity()) {
        this.showError(this.profileError, 'Please complete all required profile fields.');
        this.profileForm.reportValidity();
        return;
      }

      const recommendation = this.getRecommendation();
      const targetUrl = recommendation?.url || this.dataset.fallbackUrl;

      if (!targetUrl) {
        this.showError(this.profileError, 'Please select recommendation collections in the theme customizer.');
        return;
      }

      if (this.generateButton) {
        this.generateButton.disabled = true;
        const label = this.generateButton.querySelector('span');
        if (label) label.textContent = 'Opening suggested collection…';
      }

      window.location.assign(targetUrl);
    }

    getRecommendation() {
      if (!this.recommendations.length) return null;
      const key = this.calculateRecommendationKey();
      return this.recommendations.find((item) => Number(item.key) === key) || null;
    }

    calculateRecommendationKey() {
      const normalizedName = (this.details.name || '').toLowerCase().replace(/[^a-z]/g, '');
      const nameValue = Array.from(normalizedName).reduce((sum, letter) => sum + letter.charCodeAt(0) - 96, 0);
      const dateValue = Array.from(this.details.date || '').reduce((sum, character) => {
        const number = Number(character);
        return sum + (Number.isNaN(number) ? 0 : number);
      }, 0);
      const timeValue = this.details.time
        ? this.details.time.split(':').reduce((sum, part) => sum + Number(part || 0), 0)
        : 0;
      const total = nameValue + dateValue + timeValue;
      return ((Math.max(total, 1) - 1) % 9) + 1;
    }

    updateSummary() {
      const name = this.querySelector('[data-rf-summary-name]');
      const date = this.querySelector('[data-rf-summary-date]');
      const time = this.querySelector('[data-rf-summary-time]');

      if (name) name.textContent = this.details.name;
      if (date) date.textContent = this.formatDate(this.details.date);
      if (time) time.textContent = this.details.timeUnknown ? 'Not sure' : this.formatTime(this.details.time);
    }

    formatDate(value) {
      if (!value) return '';
      const parts = value.split('-').map(Number);
      if (parts.length !== 3) return value;
      return new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(new Date(parts[0], parts[1] - 1, parts[2]));
    }

    formatTime(value) {
      if (!value) return 'Not sure';
      const parts = value.split(':').map(Number);
      const date = new Date(2000, 0, 1, parts[0] || 0, parts[1] || 0);
      return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit'
      }).format(date);
    }

    showStartStep() {
      this.clearErrors();
      this.profileForm.hidden = true;
      this.startForm.hidden = false;
      this.nameInput?.focus({ preventScroll: true });
      this.scrollIntoViewIfNeeded();
    }

    scrollIntoViewIfNeeded() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const bounds = this.getBoundingClientRect();
      if (bounds.top < 0 || bounds.top > window.innerHeight * 0.45) {
        this.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      }
    }

    showError(element, message) {
      if (!element) return;
      element.textContent = message;
      element.hidden = false;
    }

    clearErrors() {
      [this.startError, this.profileError].forEach((element) => {
        if (!element) return;
        element.textContent = '';
        element.hidden = true;
      });
    }
  }

  customElements.define('rudraksha-finder', RudrakshaFinder);
})();
